import { TextInput, Button, Dropdown, Spinner } from 'flowbite-react';
import React, { ReactNode, useCallback, useEffect, useState, useMemo } from 'react';
import ListingTable from '../ListingTable/ListingTable';
import CandidateActions from './candidateActions/CandidateActions';
import CandidateCurrentStatus from './CandidateCurrentStatus';
// import CandidatePercentile from './CandidateScore';
// import CandidateSkills from './CandidateSkills';
import CandidateProfileColumn from './CandidateProfileColumn';
import { useGetOfflineDriveCandidates, useGetOfflineDriveParticipateCandidates, useGetEligibleDrives } from 'src/hooks/offlineDrive.hooks';
import { useUserContext } from 'src/context/UserProvider';
import BulkSelectAction from 'src/components/newRecruiterFlow/campus/campusCandidateListing/BulkSelectAction';
import { TbSearch } from 'react-icons/tb';
import RenderFilters from 'src/components/newRecruiterFlow/campus/campusCandidateListing/RenderFilters';
import { useParams, useSearchParams } from 'react-router-dom';
import { FilterType } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/CandidateFilters';
import { educationOptions } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/EducationFilterInput';
import { roleOptions } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/RoleFilterInput';
import { useGetCollegeName } from 'src/hooks/collegeName.hooks';
import { useGetAllSkills } from 'src/hooks/skill.hooks';
import { useGetDropdownItemsByCategory } from 'src/hooks/dropdownMenu.hooks';
import {
	ApplicationStatus,
	OfflineDriveCandidate,
	OfflineDriveCandidateStatus,
	SkillType,
	TypeOfUsers,
	type UserFiltersForApis,
} from 'types';
import { IoClose } from 'react-icons/io5';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';
import { debounce } from 'lodash';
import UseKeyBoardShortCut from 'src/utils/UseKeyBoardShortCut';
import { experienceOptions } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/ExperienceFilterInput';
import { getStudentTypeOptions } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/StudentTypeFilterInput';
import { driveUserStatusOptions } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/DriveUserStatusFilterInput';
import { roundTypeOptions } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/RoundTypeFilterInput';
import CandidateActiveCompanies from './CandidateActiveCompanies';
import dayjs from 'dayjs';
import CandidatesPositionPageHeader from './CandidatesPositionPageHeader';
import { TablePagination, useMediaQuery } from '@mui/material';
import { FaLink } from 'react-icons/fa';
import { driveUserDocumentOptions } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/DriveUserDocumentFilterInput';
import { driveUserISAStatusOptions } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/DriveUserISAStatusFilterInput';
import { driveUserNBFCStatusOptions } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/DriveUserNBFCStatusFilterInput';
import classNames from 'classnames';
import MobileCardForOfflineDriveCandidate from './offlineDriveCandidateMobile/MobileCardForOfflineDriveCandidate';
import { userPapStatusOptions } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/DriveUserPapStatusFilterInput';
import { opportunityStatusOptions } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/DriveOpportunityStatusFilterInput';
import { userInterviewLabelOptions } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/DriveUserInterviewLabelFilterInput';
import { userOfferStatusOptions } from 'src/components/newRecruiterFlow/campus/campusCandidateListing/DriveUserOfferStatusFilterInput';
import { useCSVDownloader } from 'react-papaparse';
import { BiDownload } from 'react-icons/bi';
import DriveCandidateSortFilterInput from 'src/components/newRecruiterFlow/campus/campusCandidateListing/DriveCandidateSortFilterInput';

/** CSV row shaping lives in API (`buildOfflineDriveCandidateCsvRows`); this only normalizes drive select label for filename fallbacks. */
function selectedDriveLabelAsString(
	label: string | { label?: string } | null | undefined,
): string {
	if (label == null) return '';
	if (typeof label === 'string') return label.trim();
	if (typeof label === 'object' && typeof label.label === 'string') return label.label.trim();
	return '';
}

const OfflineDriveCandidates = () => {
	const { CSVDownloader } = useCSVDownloader();
	const { positionId } = useParams();
	const [page, setPage] = useState(0);
	const [limit, setLimit] = useState(10);
	const isMobile = useMediaQuery('(max-width: 1279px)');
	const [searchParams, setSearchParams] = useSearchParams();
	const [showFilterDrawer, setShowFilterDrawer] = useState(false);
	const { selectedDrive, bulkSelectIds, setBulkSelectIds } = useUserContext();
	const [collegeOptions, setCollegeOptions] = React.useState<{ label: string; value: string }[]>([]);
	const bulkSelect = searchParams.get('bulkSelect') === 'true';

	UseKeyBoardShortCut(
		'#search-candidate',
		{ metaKey: true, key: 'k' },
		(element: HTMLElement | Document | React.RefObject<HTMLElement>) => {
			if (element instanceof HTMLElement) {
				element.focus();
			}
		},
	);

	const { data: skillsData } = useGetAllSkills({ type: SkillType.SKILL });
	const skillNameMap = new Map(skillsData?.skills?.map(skill => [skill._id, skill.name.replaceAll('_', ' ')]));
	// Fetch SubRole items from database (all active items, not filtered by showInMenu - for filtering)
	const { data: subRoleData } = useGetDropdownItemsByCategory('SUB_ROLE', undefined, true);
	const studentTypeOptions = getStudentTypeOptions(subRoleData);

	// Fetch placement drive labels from dropdown menu for filter
	const { data: placementDriveData } = useGetDropdownItemsByCategory('PLACEMENT_DRIVE', undefined, true);
	const { data: eligibleDrivesData } = useGetEligibleDrives({ driveId: selectedDrive?.value || undefined });

	// Get drive label options for filter
	const driveLabelFilterOptions = useMemo(() => {
		if (!selectedDrive?.value || !eligibleDrivesData?.eligibleDrives) return [];
		const selectedDriveDetails = eligibleDrivesData.eligibleDrives.find((drive: any) => drive._id === selectedDrive.value);
		if (!selectedDriveDetails?.driveLabel) return [];

		const driveLabels: string[] = Array.isArray(selectedDriveDetails.driveLabel)
			? selectedDriveDetails.driveLabel
			: [String(selectedDriveDetails.driveLabel)];

		if (placementDriveData?.lmsTechStacks && placementDriveData.lmsTechStacks.length > 0) {
			const placementDriveMap = new Map<string, string>();
			placementDriveData.lmsTechStacks
				.filter(item => item.isActive)
				.forEach(item => {
					const itemLabel = item.label || item.name;
					placementDriveMap.set(itemLabel, itemLabel);
					if (item.name && item.name !== itemLabel) {
						placementDriveMap.set(item.name, itemLabel);
					}
				});

			return driveLabels
				.map(driveLabel => {
					const matchedLabel = placementDriveMap.get(driveLabel) || driveLabel;
					return {
						label: matchedLabel,
						value: matchedLabel,
					};
				})
				.filter((option, index, self) => {
					const firstIndex = self.findIndex(o => o.label === option.label);
					return index === firstIndex;
				});
		}

		return driveLabels.map(label => ({
			label: label,
			value: label,
		}));
	}, [selectedDrive, eligibleDrivesData, placementDriveData]);

	useGetCollegeName({
		onComplete: data => {
			const newColleges = data.collegeNames;
			const uniqueColleges = Array.from(new Set(newColleges.map(c => c.name)))
				.map(name => newColleges.find(c => c.name === name)!)
				.map(c => ({
					label: c.name,
					value: c._id,
				}));
			setCollegeOptions(uniqueColleges);
		},
	});

	const driveCandidateSortParam = searchParams.get('driveCandidateSort');
	const driveCandidateSortFilter: UserFiltersForApis['driveCandidateSort'] =
		driveCandidateSortParam === 'name_asc' ||
			driveCandidateSortParam === 'name_desc' ||
			driveCandidateSortParam === 'registered_asc' ||
			driveCandidateSortParam === 'registered_desc'
			? driveCandidateSortParam
			: undefined;

	const { data: offlineDriveCandidatesData, isLoading } = useGetOfflineDriveCandidates({
		driveId: selectedDrive?.value || '',
		positionId: positionId || '',
		page: page,
		limit: limit,
		filters: {
			search: searchParams.get('search') || '',
			roles: JSON.parse(searchParams.get('role') || '[]'),
			skills: JSON.parse(searchParams.get('skills') || '[]'),
			education: JSON.parse(searchParams.get('education') || '[]'),
			papStatus: JSON.parse(searchParams.get('papStatus') || '[]'),
			roundType: JSON.parse(searchParams.get('roundType') || '[]'),
			experience: JSON.parse(searchParams.get('experience') || '[]'),
			studentType: JSON.parse(searchParams.get('studentType') || '[]'),
			offerStatus: JSON.parse(searchParams.get('offerStatus') || '[]'),
			driveUserStatus: JSON.parse(searchParams.get('userStatus') || '[]'),
			driveUserISAStatus: JSON.parse(searchParams.get('isaStatus') || '[]'),
			driveUserDocument: JSON.parse(searchParams.get('userDocument') || '[]'),
			driveUserNBFCStatus: JSON.parse(searchParams.get('nbfcStatus') || '[]'),
			opportunityStatus: JSON.parse(searchParams.get('opportunityStatus') || '[]'),
			userInterviewLabel: JSON.parse(searchParams.get('userInterviewLabel') || '[]'),
			driveLabel: JSON.parse(searchParams.get('driveLabel') || '[]'),
			...(driveCandidateSortFilter ? { driveCandidateSort: driveCandidateSortFilter } : {}),
		},
	});
	const { data: offlineDriveParticipateCandidatesData } = useGetOfflineDriveParticipateCandidates({
		driveId: selectedDrive?.value || '',
		positionId: positionId || '',
		filters: {
			search: searchParams.get('search') || '',
			roles: JSON.parse(searchParams.get('role') || '[]'),
			skills: JSON.parse(searchParams.get('skills') || '[]'),
			education: JSON.parse(searchParams.get('education') || '[]'),
			papStatus: JSON.parse(searchParams.get('papStatus') || '[]'),
			roundType: JSON.parse(searchParams.get('roundType') || '[]'),
			experience: JSON.parse(searchParams.get('experience') || '[]'),
			studentType: JSON.parse(searchParams.get('studentType') || '[]'),
			offerStatus: JSON.parse(searchParams.get('offerStatus') || '[]'),
			driveUserStatus: JSON.parse(searchParams.get('userStatus') || '[]'),
			driveUserISAStatus: JSON.parse(searchParams.get('isaStatus') || '[]'),
			driveUserDocument: JSON.parse(searchParams.get('userDocument') || '[]'),
			driveUserNBFCStatus: JSON.parse(searchParams.get('nbfcStatus') || '[]'),
			opportunityStatus: JSON.parse(searchParams.get('opportunityStatus') || '[]'),
			userInterviewLabel: JSON.parse(searchParams.get('userInterviewLabel') || '[]'),
			driveLabel: JSON.parse(searchParams.get('driveLabel') || '[]'),
			...(driveCandidateSortFilter ? { driveCandidateSort: driveCandidateSortFilter } : {}),
		},
	});

	const candidatesCsvFilename = useMemo(() => {
		const fromApi = offlineDriveParticipateCandidatesData?.driveName?.trim();
		if (fromApi) {
			return fromApi.replace(/[/\\?%*:|"<>]/g, '-');
		}
		const labelStr = selectedDriveLabelAsString(selectedDrive?.label as string | { label?: string } | undefined);
		if (labelStr) {
			return labelStr.replace(/[/\\?%*:|"<>]/g, '-');
		}
		const id = selectedDrive?.value;
		return id ? `registered-candidates-${id}` : 'registered-candidates';
	}, [offlineDriveParticipateCandidatesData?.driveName, selectedDrive?.label, selectedDrive?.value]);

	const csvFilenameWithExtension = useMemo(() => {
		const base = candidatesCsvFilename?.trim() || 'registered-candidates';
		const safe = base.replace(/[/\\?%*:|"<>]/g, '-');
		return /\.csv$/i.test(safe) ? safe : `${safe}.csv`;
	}, [candidatesCsvFilename]);

	const csvExportRows = useMemo(
		() => offlineDriveParticipateCandidatesData?.csvRows ?? [],
		[offlineDriveParticipateCandidatesData?.csvRows],
	);
	useEffect(() => {
		setPage(0);
	}, [selectedDrive?.value]);

	useEffect(() => {
		if (offlineDriveCandidatesData) {
			setPage(offlineDriveCandidatesData?.pagination?.currentPage || 0);
		}
	}, [offlineDriveCandidatesData]);

	const tableHeaders: { id: string; label: string; align?: 'center' | 'left' | 'right' | 'inherit' | 'justify' }[] = [
		{ id: 'candidate', label: 'Candidate', align: 'left' },
		// { id: 'role', label: 'Role' }
		{ id: 'driveLabel', label: 'Drive Label', align: 'center' },
		{ id: 'resueme', label: 'Resume', align: 'center' },
		// { id: 'score', label: 'Score' },
		// { id: 'skill_level', label: 'Skill Level', align: 'center' },
		{ id: 'activeCompanies', label: 'Active Companies' },
		{ id: 'opportunities', label: 'Opportunities' },
		{ id: 'actions', label: 'Actions' },
	];

	const [tableRows, setTableRows] = useState<{ id: string; tableDataJsx: React.ReactNode }[][]>([]);

	useEffect(() => {
		const allTableRows: {
			id: string;
			bgColor?: string;
			tableDataJsx: ReactNode;
		}[][] =
			offlineDriveCandidatesData?.candidates?.map((candidate, index) => {
				const isNotAvailableForLong: number =
					candidate?.status === OfflineDriveCandidateStatus.BUSY ||
						candidate?.status === OfflineDriveCandidateStatus.ASSIGN
						? dayjs().diff(
							dayjs(
								candidate?.offlineDriveCandidateStatusHistory?.sort(
									(a, b) => dayjs(b.updatedAt).unix() - dayjs(a.updatedAt).unix(),
								)[0]?.updatedAt,
							),
							'minutes',
						)
						: 0;

				return [
					{
						id: candidate?._id,
						bgColor: isNotAvailableForLong >= 60 ? 'rgba(255, 229, 229, 0.50)' : '',
						status: candidate?.status,
						tableDataJsx: (
							<div>
								<CandidateProfileColumn
									offlineDriveRoom={candidate?.offlineDriveRoom}
									name={candidate?.name}
									email={candidate?.email}
									image={candidate?.image}
									handler={candidate?.handler}
									studentCode={candidate?.studentCode}
									attendDriveDate={candidate?.offlineDriveCandidateStatusHistory?.sort(
										(a, b) => dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix()
									)[0]?.updatedAt}
									phone={candidate?.phone?.toString()}
									showLink={true}
									status={candidate?.status}
									documents={candidate?.documents}
								/>
							</div>
						),
					},
					{
						id: 'driveLabel',
						tableDataJsx: (
							<div className="text-center">
								{candidate?.driveLabel ? (
									<span className="text-sm text-gray-700 dark:text-gray-300">{candidate.driveLabel}</span>
								) : (
									<span className="text-sm text-gray-400">-</span>
								)}
							</div>
						),
					},
					{
						id: 'resume',
						tableDataJsx: (
							<div className="m-auto">
								<a target="_blank" rel="noreferrer" href={candidate?.resume}>
									<FaLink className="mx-auto" />
								</a>
							</div>
						),
					},
					// {
					// 	id: 'role',
					// 	tableDataJsx: (
					// 		<div>
					// 			<CandidateCareerPath role={candidate.careerPath} />
					// 		</div>
					// 	),
					// },
					// {
					// 	id: 'score',
					// 	tableDataJsx: (
					// 		<div>
					// 			<CandidatePercentile score={candidate.score} />
					// 		</div>
					// 	),
					// },
					// {
					// 	id: 'skills',
					// 	align: 'center',
					// 	tableDataJsx: (
					// 		<div>
					// 			<CandidateSkills skillRatings={candidate?.skillRatings || []} />
					// 		</div>
					// 	),
					// },
					{
						id: 'activeCompanies',
						tableDataJsx: (
							<div>
								<CandidateActiveCompanies
									activeCompanies={candidate?.activeCompanies}
									offerRolledOutCompany={candidate?.roundsDetails
										?.filter(round => round?.application?.status === ApplicationStatus.OFFER_ROLLED_OUT)
										?.map(round => round?.company?._id)}
								/>
							</div>
						),
					},
					{
						id: 'currentStatus',
						tableDataJsx: (
							<div>
								<CandidateCurrentStatus candidate={candidate} />
							</div>
						),
					},
					{
						id: 'actions',
						tableDataJsx: (
							<div>
								<CandidateActions driveId={selectedDrive?.value || ''} candidate={candidate} />
							</div>
						),
					},
				];
			}) || [];
		setTableRows(allTableRows);
	}, [offlineDriveCandidatesData]);

	const [appliedFilters, setAppliedFilters] = useState<FilterType>([]);

	useEffect(() => {
		const filters: FilterType = [];

		// Get skill filter
		const skill = JSON.parse(searchParams.get('skills') || '[]');
		if (skill.length) {
			filters.push({
				filterKey: 'skills',
				data: skill.map((id: string) => ({
					label: skillNameMap.get(id) || id, // Use skill name or ID if not found
					value: id,
				})),
			});
		}

		// Get role filter
		const role = JSON.parse(searchParams.get('role') || '[]');
		if (role.length) {
			filters.push({
				filterKey: 'role',
				data: roleOptions
					?.filter(option => role?.includes(option?.value))
					?.map(option => ({
						label: option?.label,
						value: option?.value,
					})),
			});
		}

		// Get education filter
		const education = JSON.parse(searchParams.get('education') || '[]');
		if (education.length) {
			filters.push({
				filterKey: 'education',
				data: educationOptions
					?.filter(option => education?.includes(option))
					?.map(option => ({
						label: option,
						value: option,
					})),
			});
		}

		// Get experience filter
		const experience = JSON.parse(searchParams.get('experience') || '[]');
		if (experience.length) {
			filters.push({
				filterKey: 'experience',
				data: experienceOptions?.filter(option => experience?.includes(option?.value)),
			});
		}

		// Get studentType filter
		const studentType = JSON.parse(searchParams.get('studentType') || '[]');
		if (studentType.length) {
			filters.push({
				filterKey: 'studentType',
				data: studentTypeOptions?.filter((option: { value: string; label: string }) => studentType?.includes(option?.value)),
			});
		}

		// Get userStatus filter
		const userStatus = JSON.parse(searchParams.get('userStatus') || '[]');
		if (userStatus.length) {
			filters.push({
				filterKey: 'userStatus',
				data: driveUserStatusOptions?.filter(option => userStatus?.includes(option?.value)),
			});
		}

		// Get roundType filter
		const roundType = JSON.parse(searchParams.get('roundType') || '[]');
		if (roundType.length) {
			filters.push({
				filterKey: 'roundType',
				data: roundTypeOptions?.filter(option => roundType?.includes(option?.value)),
			});
		}

		// Get userDocument filter
		const userDocument = JSON.parse(searchParams.get('userDocument') || '[]');
		if (userDocument.length) {
			filters.push({
				filterKey: 'userDocument',
				data: driveUserDocumentOptions?.filter(option => userDocument?.includes(option?.value)),
			});
		}

		// Get isaStatus filter
		const isaStatus = JSON.parse(searchParams.get('isaStatus') || '[]');
		if (isaStatus.length) {
			filters.push({
				filterKey: 'isaStatus',
				data: driveUserISAStatusOptions?.filter(option => isaStatus?.includes(option?.value)),
			});
		}

		// Get nbfcStatus filter
		const nbfcStatus = JSON.parse(searchParams.get('nbfcStatus') || '[]');
		if (nbfcStatus.length) {
			filters.push({
				filterKey: 'nbfcStatus',
				data: driveUserNBFCStatusOptions?.filter(option => nbfcStatus?.includes(option?.value)),
			});
		}

		// Get papStatus filter
		const papStatus = JSON.parse(searchParams.get('papStatus') || '[]');
		if (papStatus.length) {
			filters.push({
				filterKey: 'papStatus',
				data: userPapStatusOptions?.filter(option => papStatus?.includes(option?.value)),
			});
		}

		// Get opportunityStatus filter
		const opportunityStatus = JSON.parse(searchParams.get('opportunityStatus') || '[]');
		if (opportunityStatus.length) {
			filters.push({
				filterKey: 'opportunityStatus',
				data: opportunityStatusOptions?.filter(option => opportunityStatus?.includes(option?.value)),
			});
		}

		// Get userInterviewLabel filter
		const userInterviewLabel = JSON.parse(searchParams.get('userInterviewLabel') || '[]');
		if (userInterviewLabel.length) {
			filters.push({
				filterKey: 'userInterviewLabel',
				data: userInterviewLabelOptions?.filter(option => userInterviewLabel?.includes(option?.value)),
			});
		}

		// Get driveLabel filter
		const driveLabel = JSON.parse(searchParams.get('driveLabel') || '[]');
		if (driveLabel.length) {
			filters.push({
				filterKey: 'driveLabel',
				data: driveLabelFilterOptions?.filter((option: { label: string; value: string }) => driveLabel?.includes(option?.value)),
			});
		}

		// Get offerStatus filter
		const offerStatus = JSON.parse(searchParams.get('offerStatus') || '[]');
		if (offerStatus.length) {
			filters.push({
				filterKey: 'offerStatus',
				data: userOfferStatusOptions?.filter(option => offerStatus?.includes(option?.value)),
			});
		}

		setAppliedFilters(filters);

		const bulkSelect = searchParams.get('bulkSelect') === 'true';

		// Check if any other search parameters have changed
		const otherParamsChanged = [
			'skills',
			'salary',
			'role',
			'education',
			'studentType',
			'search',
			'userStatus',
			'userDocument',
			'roundType',
			'isaStatus',
			'papStatus',
			'nbfcStatus',
			'opportunityStatus',
			'userInterviewLabel',
			'driveCandidateSort',
		].some(param => searchParams.has(param));

		if (!bulkSelect && otherParamsChanged) {
			setPage(0);
		}
	}, [searchParams, skillsData, collegeOptions]);

	const removeFilter = (filterKey: string, value?: string) => {
		if (value) {
			const currentValues = JSON.parse(searchParams.get(filterKey) || '[]');
			const updatedValues = currentValues.filter((v: string) => v !== value);
			if (updatedValues.length > 0) {
				searchParams.set(filterKey, JSON.stringify(updatedValues));
			} else {
				searchParams.delete(filterKey);
			}
			setSearchParams(searchParams);
		}
	};

	const onSelectAllCandidates = () => {
		if (offlineDriveCandidatesData?.candidates?.length === bulkSelectIds.length) {
			setBulkSelectIds([]);
		} else {
			const candidateIds = offlineDriveCandidatesData?.candidates?.map(candidate => candidate?._id) || [];
			setBulkSelectIds(candidateIds);
		}
	};

	const changeHandler = (search: string) => {
		setPage(0);
		searchParams.set('search', search);
		setSearchParams(searchParams);
	};

	const debouncedChangeHandler = useCallback(debounce(changeHandler, 1000), [positionId, searchParams]);

	const FilterComponent = () => (
		<RenderFilters
			filterName={isMobile ? '' : 'Filters'}
			showCampusFilter={false}
			showEducationFilter={true}
			showLocationFilter={true}
			showAvailabilityFilter={true}
			showRoleFilter={true}
			showSkillFilter={true}
			showMatchFilter={false}
			showStatusFilter={false}
			showSalaryFilter={false}
			showExperienceFilter={true}
			showStudentTypeFilter={true}
			showRoundTypeFilter={true}
			showDriveUserStatusFilter={true}
			showDriveUserDocumentFilter={true}
			showIsaStatusFilter={false}
			showNbfcStatusFilter={true}
			showPapStatusFilter={true}
			showOfferStatusFilter={true}
			showOpportunityStatusFilter={true}
			showUserInterviewLabelFilter={true}
			showDriveLabelFilter={true}
			filterTrailingSlot={({ forDrawer }) =>
				forDrawer ? (
					<DriveCandidateSortFilterInput showSelect />
				) : (
					<>
						<div className="text-[#28293D] dark:text-[#C7C9D9] text-sm font-[500] shrink-0">Sort by:</div>
						<div className="min-w-[200px] max-w-[260px]">
							<DriveCandidateSortFilterInput />
						</div>
					</>
				)
			}
			appliedFilters={appliedFilters}
			setAppliedFilters={setAppliedFilters}
			showFilterDrawer={showFilterDrawer}
			setShowFilterDrawer={setShowFilterDrawer}
			filterOrder={[
				'role',
				'skill',
				'experience',
				'education',
				'studentType',
				'driveUserStatus',
				'driveUserDocument',
				'userInterviewLabel',
				'driveLabel',
				'nbfcStatus',
				'papStatus',
				'roundType',
				'opportunityStatus',
				'offerStatus',
			]}
		/>
	);

	return (
		<div className="py-4 md:py-[30px] max-w-[1440px] px-4 xl:px-10 mx-auto">
			{positionId ? (
				<CandidatesPositionPageHeader
					positionId={positionId}
					totalCandidates={offlineDriveCandidatesData?.pagination?.totalItems || 0}
				/>
			) : (
				<div className="text-base md:text-xl font-[600] text-[#1C1C28] pb-4 md:pb-[20px] border-b border-[#E4E4EB] mb-4">
					Registered Candidates ({offlineDriveCandidatesData?.pagination?.totalItems || 0})
				</div>
			)}

			<div className="flex items-center flex-wrap gap-3">
				<TextInput
					id="search-candidate"
					placeholder="Search candidate by name, email, phone or student code..."
					className="w-full max-w-[731px] [&_input]:bg-white flex-1 md:flex-none"
					icon={() => <TbSearch color="#C7C9D9" />}
					onChange={e => {
						debouncedChangeHandler(e.target.value);
					}}
				/>
				<div>
					<BulkSelectAction
						typeOfUsers={TypeOfUsers.DRIVE}
						bulkSelectedIds={bulkSelectIds}
						setBulkSelectedIds={setBulkSelectIds}
					/>
				</div>
				{isMobile ? <div className="xl:hidden">{FilterComponent()}</div> : null}
				<div
					className="ml-[auto] [&_div[data-testid='flowbite-dropdown']]:!border-gray-200"
				>
					<Dropdown
						arrowIcon={false}
						color={'#C7C9D9'}
						className="dark:!bg-[#111E2C] !text-[#E4E4EB] rounded-[8px]"
						label=""
						dismissOnClick
						renderTrigger={() => (
							<img className="h-[24px] cursor-pointer" src="/assets/three-dots-vertical.svg" alt="menu" />
						)}
					>
						<Dropdown.Item
							className={'dark:!text-[#C7C9D9] dark:hover:!bg-[#2c3c50]'}
						>
							<div>
								<CSVDownloader
									filename={csvFilenameWithExtension}
									bom={true}
									data={csvExportRows}
									className="flex justify-center items-center"
								>
									<BiDownload className="w-[16px] h-[16px]" />
									<p className="ml-[8px] text-[14px] font-[500] leading-[21px]">Download Candidates</p>
								</CSVDownloader>
							</div>
						</Dropdown.Item>
					</Dropdown>
				</div>
			</div>

			<div className="py-2 xl:py-4">
				{isMobile ? null : <div className="hidden xl:block">{FilterComponent()}</div>}

				{appliedFilters.length > 0 && (
					<div className="mt-[12px] flex-wrap gap-4 hidden xl:flex">
						{appliedFilters
							?.filter(el => el?.data?.length)
							.map(filter => (
								<div key={filter.filterKey} className="flex flex-wrap items-center gap-4">
									{filter.data.map((value, index) => (
										<div
											key={index}
											className="text-sm font-[500] flex gap-1 bg-[#E4E4EB] text-[#555770] pl-[6px] rounded-[4px]"
										>
											<span className="py-[2px] max-w-[30ch] truncate">{value?.label}</span>
											<button
												onClick={() => removeFilter(filter.filterKey, value?.value)}
												className="hover:bg-gray-300 active:bg-gray-400 px-[6px] rounded-r-[4px]"
											>
												<IoClose className="w-[12px] h-[12px]" />
											</button>
										</div>
									))}
								</div>
							))}
					</div>
				)}
			</div>

			{isLoading ? (
				<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="md:!w-[35%] mx-auto" />
			) : (
				<>
					{!isMobile ? (
						<div className="w-full">
							{selectedDrive && (
								<ListingTable
									page={page}
									setPage={setPage}
									showPagination={true}
									tableRows={tableRows}
									setRowsPerPage={setLimit}
									tableHeaders={tableHeaders}
									showBulkSelectAction={bulkSelect}
									onSelectAllCandidates={onSelectAllCandidates}
									count={offlineDriveCandidatesData?.pagination?.totalItems || 0}
									rowsPerPage={offlineDriveCandidatesData?.pagination?.itemsPerPage || 10}
								/>
							)}
						</div>
					) : (
						<div className={classNames(bulkSelect && bulkSelectIds?.length ? 'pb-[40vh]' : '')}>
							<div className="flex flex-col gap-4">
								{offlineDriveCandidatesData?.candidates.map((candidate: OfflineDriveCandidate) => {
									return (
										<div>
											<MobileCardForOfflineDriveCandidate candidate={candidate} bulkSelect={bulkSelect} attendDriveDate={candidate?.offlineDriveCandidateStatusHistory?.sort(
												(a, b) => dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix()
											)[0]?.updatedAt} />
										</div>
									);
								})}
							</div>

							<div className="flex justify-center items-center w-full mt-4">
								<TablePagination
									sx={{
										'&': {
											background: '#FAFAFC',
											'[data-mode="dark"] &': {
												background: '#18222D',
											},
										},
									}}
									rowsPerPageOptions={[5, 10, 25, 100]}
									component="div"
									count={offlineDriveCandidatesData?.pagination?.totalItems || 0}
									rowsPerPage={offlineDriveCandidatesData?.pagination?.itemsPerPage || 10}
									page={page}
									onPageChange={(event: unknown, newPage: number) => {
										setPage && setPage(newPage);
									}}
									onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => {
										setLimit(parseInt(event.target.value, 10));
										setPage && setPage(0);
									}}
								/>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default OfflineDriveCandidates;
